/**
 * Match-quality scoring for global search (需求 FR-14 §4.2.1 前缀匹配优先).
 *
 * **谁给分，分工是清楚的**：平台只给**自己匹配出来的东西**打分（命令、`/open` 行——它认识
 * 这些文本，也知道用户敲了什么）；模块返回的命中行由**模块自己**打分，平台原样采纳、只做
 * 稳定排序。理由是相关度需要领域知识：一条消息里关键词出现在正文还是引用、发送者是不是本人、
 * 会话是不是置顶，只有模块知道；平台去猜只会猜错。这个函数因此导出给两边共用——模块想用
 * 同一套「精确 / 前缀 / 词界 / 包含」口径时不必重写一遍。
 *
 * 刻意不做 TF-IDF：那是为上千条候选的召回准备的，这里的规模是几十条，靠分档 + 位置 +
 * 覆盖率已经够，且每一档都能向用户解释。
 */

/** 命中在文本开头。 */
const TIER_PREFIX = 600;
/** 命中在一个词的开头（前一个字符不是字母数字）。 */
const TIER_WORD_START = 400;
/** 命中在词中间。 */
const TIER_CONTAINS = 200;
/** 整段文本与查询完全相等时的额外加成。 */
const BONUS_EXACT = 1000;
/** 覆盖率（查询长度 / 文本长度）的满额加成。 */
const BONUS_COVERAGE = 200;
/** 位置惩罚的上限：越靠后越差，但差到一定程度就不再拉开。 */
const MAX_POSITION_PENALTY = 40;

/** 是否落在词首。CJK 没有空格，这类文本靠位置惩罚区分，不靠这里。 */
function isWordStart(text: string, at: number): boolean {
  if (at === 0) {
    return true;
  }
  return /[^\p{L}\p{N}]/u.test(text[at - 1]);
}

/**
 * 一段文本对一组查询词的匹配度，越大越相关；**任一词不出现即 0**。
 *
 * `terms` 应当已经是小写分词（`SearchQuery.terms`）。空 `terms` 回 0——「没有查询」谈不上
 * 相关度，此时该由调用方用别的信号排序（命令面用最近使用）。
 */
export function matchScore(text: string, terms: readonly string[]): number {
  if (terms.length === 0 || text.length === 0) {
    return 0;
  }
  const haystack = text.toLowerCase();
  let tiers = 0;
  for (const term of terms) {
    const at = haystack.indexOf(term);
    if (at < 0) {
      return 0;
    }
    const tier =
      at === 0
        ? TIER_PREFIX
        : isWordStart(haystack, at)
          ? TIER_WORD_START
          : TIER_CONTAINS;
    tiers += tier - Math.min(at, MAX_POSITION_PENALTY);
  }
  const joined = terms.join(' ');
  let score = tiers / terms.length;
  if (haystack === joined) {
    score += BONUS_EXACT;
  }
  // 覆盖率：同样命中时，越短的文本是越紧的匹配。这条正是短查询能被找到的关键——搜「1」时
  // 一条正文只有「1」的消息该排在一篇长文之前，否则它永远沉在底下（实测报过这个问题）。
  score += Math.round(
    (joined.length / Math.max(haystack.length, 1)) * BONUS_COVERAGE,
  );
  return score;
}

/**
 * 多个字段里最好的那个分，次要字段按 `penalty` 降权。
 *
 * 用来表达「标题命中比说明命中更相关」，而不必在调用处堆 `Math.max`。
 */
export function bestMatchScore(
  fields: readonly { text?: string; penalty?: number }[],
  terms: readonly string[],
): number {
  let best = 0;
  for (const field of fields) {
    if (!field.text) {
      continue;
    }
    const score = matchScore(field.text, terms);
    if (score > 0) {
      best = Math.max(best, score - (field.penalty ?? 0));
    }
  }
  return best;
}
